import { useTranslation } from 'react-i18next';
import { firstDiffIndex } from './verify';

/**
 * Payment-Link Link-Tester — „So haben wir gerechnet" (Spec 501, Phase 6).
 *
 * Nummerierte Schritte: String-to-Sign → ASCII-Schluessel-Didaktik (generisches
 * Beispiel, NIE der echte Salt) → erwartete Signatur → erwartet-vs-geliefert mit
 * Hervorhebung der ersten Abweichungsstelle. Geteilt von Modus A/B.
 */
export interface CalculationDetailsProps {
  stringToSign: string;
  expected: string;
  /** Gelieferte (normalisierte) Signatur — nur im Prüf-Modus. */
  received?: string;
}

export function CalculationDetails({
  stringToSign,
  expected,
  received,
}: CalculationDetailsProps) {
  const { t } = useTranslation('payment-link');
  const diffAt =
    received !== undefined ? firstDiffIndex(expected, received) : -1;

  return (
    <details
      className="rounded-md border p-3 text-sm"
      data-testid="calculation-details"
    >
      <summary className="cursor-pointer font-medium">
        {t('tester.calc.title')}
      </summary>
      <ol className="mt-3 list-decimal space-y-3 pl-5">
        <li>
          <div className="mb-1 text-xs text-muted-foreground">
            {t('tester.calc.stepStringToSign')}
          </div>
          <code className="block break-all rounded bg-muted p-2 font-mono text-xs">
            {stringToSign}
          </code>
        </li>
        <li>
          <div className="text-xs text-muted-foreground">
            {t('tester.calc.stepAsciiKey')}
          </div>
        </li>
        <li>
          <div className="mb-1 text-xs text-muted-foreground">
            {t('tester.calc.stepExpected')}
          </div>
          <code className="block break-all rounded bg-muted p-2 font-mono text-xs">
            {expected}
          </code>
        </li>
        {received !== undefined && (
          <li>
            <div className="mb-1 text-xs text-muted-foreground">
              {t('tester.calc.stepCompare')}
            </div>
            <code
              className="block break-all rounded bg-muted p-2 font-mono text-xs"
              data-testid="compare-received"
            >
              {diffAt >= 0 ? (
                <>
                  <span>{received.slice(0, diffAt)}</span>
                  <span
                    className="bg-destructive/20 font-bold text-destructive"
                    data-testid="first-diff"
                  >
                    {received.slice(diffAt, diffAt + 1) || '·'}
                  </span>
                  <span>{received.slice(diffAt + 1)}</span>
                </>
              ) : (
                received
              )}
            </code>
            {diffAt >= 0 && (
              <p className="mt-1 text-xs text-destructive">
                {t('tester.calc.diffAt', { pos: diffAt + 1 })}
              </p>
            )}
          </li>
        )}
      </ol>
    </details>
  );
}
